import { getPushStatus, removePushSubscription, savePushSubscription } from "@/services";

import { isTelegramMiniApp } from "./telegram";

const SW_URL = "/sw.js";

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let installPrompt: InstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

/**
 * Registers the service worker in production builds and keeps the browser's install prompt for
 * the "Install app" button. Call once at startup: the prompt event fires early.
 */
export const initPwa = () => {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    installPrompt = event as InstallPromptEvent;
    installListeners.forEach((listener) => listener());
  });
  window.addEventListener("appinstalled", () => {
    installPrompt = null;
    installListeners.forEach((listener) => listener());
  });
  // in dev the worker is registered only when push is turned on
  if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register(SW_URL).catch((error) => {
        console.warn("Service worker registration failed:", error);
      });
    });
  }
};

export const canInstallApp = () => installPrompt !== null;

export const onInstallAvailabilityChange = (listener: () => void) => {
  installListeners.add(listener);
  return () => {
    installListeners.delete(listener);
  };
};

export const promptInstallApp = async (): Promise<boolean> => {
  if (!installPrompt) return false;
  const prompt = installPrompt;
  installPrompt = null;
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  installListeners.forEach((listener) => listener());
  return outcome === "accepted";
};

export const isStandaloneApp = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

// iOS offers push only to a site added to the home screen.
export const isIosBrowser = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) && !isStandaloneApp();

export const isPushSupported = () =>
  !isTelegramMiniApp() &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const registration = async () =>
  (await navigator.serviceWorker.getRegistration()) ??
  (await navigator.serviceWorker.register(SW_URL));

const currentSubscription = async () => {
  const existing = await navigator.serviceWorker.getRegistration();
  return existing ? await existing.pushManager.getSubscription() : null;
};

const keyBytes = (base64url: string) => {
  const base64 = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
};

const sameKey = (subscription: PushSubscription, publicKey: string) => {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = keyBytes(publicKey);
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

export interface DevicePushState {
  supported: boolean;
  // the server has VAPID keys
  available: boolean;
  subscribed: boolean;
  permission: NotificationPermission | "unsupported";
  publicKey: string | null;
}

export const getDevicePushState = async (): Promise<DevicePushState> => {
  if (!isPushSupported()) {
    return {
      supported: false,
      available: false,
      subscribed: false,
      permission: "unsupported",
      publicKey: null,
    };
  }
  const subscription = await currentSubscription();
  const status = await getPushStatus(subscription?.endpoint);
  return {
    supported: true,
    available: status.enabled,
    subscribed: status.subscribed && Notification.permission === "granted",
    permission: Notification.permission,
    publicKey: status.publicKey,
  };
};

/** Asks for permission, subscribes this browser and hands the subscription to the server. */
export const enablePush = async (publicKey: string): Promise<NotificationPermission> => {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission;
  const worker = await registration();
  await navigator.serviceWorker.ready;
  let subscription = await worker.pushManager.getSubscription();
  // a subscription made with old keys cannot receive messages signed with the new ones
  if (subscription && !sameKey(subscription, publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await worker.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(publicKey),
  });
  await savePushSubscription(subscription.toJSON());
  return permission;
};

export const disablePush = async (): Promise<void> => {
  const subscription = await currentSubscription();
  if (!subscription) return;
  await removePushSubscription(subscription.endpoint);
  await subscription.unsubscribe();
};

/** On sign-out: this device stops getting the player's messages. Never blocks the sign-out. */
export const releasePushOnLogout = async (): Promise<void> => {
  try {
    if (!isPushSupported()) return;
    const subscription = await currentSubscription();
    if (subscription) await removePushSubscription(subscription.endpoint);
  } catch (error) {
    console.warn("Push release on logout failed:", error);
  }
};
